import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  ArrowRight,
  Check,
  Code,
  Globe,
  Zap,
  Timer,
  Bell,
  LayoutDashboard,
  Shield,
  Rocket,
} from 'lucide-react'

export const Route = createFileRoute('/_site/guides/monitor-side-project')({
  component: MonitorSideProjectGuide,
  head: () => ({
    meta: [
      {
        title:
          'Monitor Your Side Project — Setup Guide — Uptrack',
      },
      {
        name: 'description',
        content:
          'Your side project needs monitoring too. Set up 30-second uptime checks, status pages and cron monitoring from $3/mo. Step-by-step guide.',
      },
      {
        property: 'og:title',
        content: 'Monitor Your Side Project — Uptrack',
      },
      {
        property: 'og:description',
        content:
          'Side projects deserve monitoring. Pro includes 50 monitors at 30s, status pages, and cron monitoring. Complete setup guide.',
      },
      {
        property: 'og:image',
        content: 'https://uptrack.app/og/guides-monitor-side-project.png',
      },
      { property: 'og:type', content: 'article' },
      {
        property: 'og:url',
        content: 'https://uptrack.app/guides/monitor-side-project',
      },
    ],
    links: [
      {
        rel: 'canonical',
        href: 'https://uptrack.app/guides/monitor-side-project',
      },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline:
            'Monitor Your Side Project — Complete Setup Guide',
          description:
            'Your side project needs monitoring too. Step-by-step guide to setting up website monitoring with Uptrack.',
          datePublished: '2026-05-22',
          dateModified: '2026-10-01',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: {
            '@type': 'Organization',
            name: 'Uptrack',
            url: 'https://uptrack.app',
          },
          mainEntityOfPage:
            'https://uptrack.app/guides/monitor-side-project',
        }),
      },
    ],
  }),
})

function MonitorSideProjectGuide() {
  return (
    <>

      <>
        <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <h1 className="font-sans mb-4 text-4xl font-semibold">
            Monitor your side project — complete setup guide
          </h1>
          <p className="mb-8 text-lg leading-[1.7] text-muted-foreground">
            You built something. Maybe it's a tool that 50 people use. Maybe
            it's a weekend project that somehow grew into something real.
            Either way, you probably don't have monitoring on it. And when
            it goes down at 2pm on a Wednesday while you're at your day job,
            your users find out before you do.
          </p>

          {/* Why side projects need monitoring */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Why side projects need monitoring (more than you think)
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              "It's just a side project" is the most common reason people
              skip monitoring. But side projects fail in ways that production
              apps don't, precisely because they get less attention.
            </p>
            <div className="space-y-4">
              {[
                {
                  title: 'You don\'t check them daily',
                  description:
                    'Production apps have teams staring at dashboards. Your side project might go unchecked for days. Without monitoring, your site could be down for a week before you notice.',
                },
                {
                  title: 'Cheap hosting is less reliable',
                  description:
                    'Your $5/mo VPS, free-tier Railway, or Render instance doesn\'t have the same uptime guarantees as your employer\'s AWS setup. These services restart, hibernate, or run out of resources more often.',
                },
                {
                  title: 'SSL certificates expire silently',
                  description:
                    'Let\'s Encrypt certificates auto-renew until they don\'t. A DNS change, a Docker rebuild, or a provider migration can break auto-renewal. Without monitoring, you\'ll find out when users report a security warning.',
                },
                {
                  title: 'Side projects become real products',
                  description:
                    'Many successful SaaS companies started as side projects. If yours starts getting users and revenue, having monitoring history from day one is invaluable. You\'ll know your actual uptime, not a guess.',
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-3">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                  <div>
                    <span className="font-semibold">{item.title}</span>
                    <span className="text-muted-foreground">
                      {' '}
                      -- {item.description}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* What an Uptrack subscription gives you */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              What you get with an Uptrack subscription
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Many free monitoring plans give you a few monitors at 5-minute
              intervals with email-only alerts. That's barely enough to
              monitor one project. Uptrack Pro costs $3/mo for 50 monitors,
              with a 30-day money-back guarantee.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-border bg-card p-5">
                <Globe className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">50 monitors</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Enough for multiple side projects. Monitor landing pages,
                  apps, APIs, and background jobs all from one account.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Zap className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">30-second checks</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Every monitor runs at 30-second intervals.
                  No speed tiers, no slot management —
                  generous check intervals for maximum detection speed.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <LayoutDashboard className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Status pages</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Create a public status page for your project. When users
                  ask "is it down?", point them to your status page instead
                  of answering individually. Pro includes 5.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Timer className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Cron monitoring</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Monitor your scheduled tasks and background jobs. If your
                  daily email cron or weekly data sync stops running, you'll
                  know immediately instead of discovering it weeks later.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Bell className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Multi-channel alerts</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Email, Slack, Discord, and webhook alerts. Every paid plan
                  includes every alert channel; the free plan has email plus one
                  chat channel. Get notified wherever you actually look.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Shield className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Consecutive-check confirmation</h3>
                <p className="text-lg leading-[1.7] text-body">
                  No false alerts. Every plan uses consecutive-
                  check confirmation. You only get alerted when something is
                  actually broken, not when the internet hiccups.
                </p>
              </div>
            </div>
          </section>

          {/* Step-by-step setup */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Step-by-step: monitor your side project in 10 minutes
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Here's exactly how to set up comprehensive monitoring for a
              typical side project. We'll assume your project has a landing
              page, an app, an API, and a background job.
            </p>

            <div className="space-y-8">
              {/* Step 1 */}
              <div>
                <div className="flex gap-4 mb-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                    1
                  </div>
                  <div>
                    <h3 className="font-sans font-semibold text-lg">
                      Create your Uptrack account
                    </h3>
                    <p className="text-lg leading-[1.7] text-body mt-1">
                      Go to uptrack.app and sign up. Start free with 5 monitors, or choose Pro for all 50
                      monitors immediately.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div>
                <div className="flex gap-4 mb-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                    2
                  </div>
                  <div>
                    <h3 className="font-sans font-semibold text-lg">
                      Add your homepage monitor (1-min interval)
                    </h3>
                    <p className="text-lg leading-[1.7] text-body mt-1">
                      Click "Create Monitor." Paste your homepage URL. Set the
                      check interval to 1 minute — this is your most important
                      page, so give it the fastest detection. Set expected status
                      code to 200. Add a body assertion for your site's name or
                      headline to catch blank-page errors.
                    </p>
                  </div>
                </div>
                <div className="ml-12 rounded-lg border border-border bg-card p-4">
                  <p className="mb-2 text-xs font-medium text-muted-foreground">
                    Example configuration
                  </p>
                  <pre tabIndex={0} className="overflow-x-auto text-sm leading-relaxed">
                    <code className="text-muted-foreground">{`URL:           https://myproject.com
Method:        GET
Interval:      1 minute
Status code:   200
Body contains: "MyProject - Build faster"`}</code>
                  </pre>
                </div>
              </div>

              {/* Step 3 */}
              <div>
                <div className="flex gap-4 mb-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                    3
                  </div>
                  <div>
                    <h3 className="font-sans font-semibold text-lg">
                      Add your app and API monitors (1-min interval)
                    </h3>
                    <p className="text-lg leading-[1.7] text-body mt-1">
                      If your app runs separately from your landing page, monitor
                      it independently. Add your app URL and your API health
                      endpoint. Use 1-minute checks for both — these are your
                      most critical endpoints. That's 3 of your available
                      monitors used.
                    </p>
                  </div>
                </div>
                <div className="ml-12 rounded-lg border border-border bg-card p-4">
                  <p className="mb-2 text-xs font-medium text-muted-foreground">
                    API health check example
                  </p>
                  <pre tabIndex={0} className="overflow-x-auto text-sm leading-relaxed">
                    <code className="text-muted-foreground">{`URL:           https://api.myproject.com/health
Method:        GET
Interval:      1 minute
Status code:   200
Body contains: "status":"ok"`}</code>
                  </pre>
                </div>
              </div>

              {/* Step 4 */}
              <div>
                <div className="flex gap-4 mb-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                    4
                  </div>
                  <div>
                    <h3 className="font-sans font-semibold text-lg">
                      Add secondary pages (2-min and 3-min intervals)
                    </h3>
                    <p className="text-lg leading-[1.7] text-body mt-1">
                      Monitor your pricing page, documentation, blog, or any
                      other pages that matter. Pro gives you 50 monitors, all at
                      30-second intervals.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div>
                <div className="flex gap-4 mb-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                    5
                  </div>
                  <div>
                    <h3 className="font-sans font-semibold text-lg">
                      Set up cron monitoring for background jobs
                    </h3>
                    <p className="text-lg leading-[1.7] text-body mt-1">
                      Create a cron monitor for each scheduled task. Uptrack
                      gives you a unique URL to ping from your cron job. If the
                      ping doesn't arrive on schedule, you get alerted. Add a
                      simple HTTP call at the end of your job.
                    </p>
                  </div>
                </div>
                <div className="ml-12 rounded-lg border border-border bg-card p-4">
                  <p className="mb-2 text-xs font-medium text-muted-foreground">
                    Add to your cron job script
                  </p>
                  <pre tabIndex={0} className="overflow-x-auto text-sm leading-relaxed">
                    <code className="text-muted-foreground">{`# At the end of your cron job:
curl -fsS --retry 3 https://uptrack.app/ping/YOUR_CRON_ID

# Or in Node.js:
await fetch("https://uptrack.app/ping/YOUR_CRON_ID")

# Or in Python:
requests.get("https://uptrack.app/ping/YOUR_CRON_ID")`}</code>
                  </pre>
                </div>
              </div>

              {/* Step 6 */}
              <div>
                <div className="flex gap-4 mb-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                    6
                  </div>
                  <div>
                    <h3 className="font-sans font-semibold text-lg">
                      Configure your alert channels
                    </h3>
                    <p className="text-lg leading-[1.7] text-body mt-1">
                      Set up at least two alert channels. Email as a fallback,
                      plus Slack or Discord for instant notifications. If you
                      have a project Discord server, add a #monitoring channel
                      and connect Uptrack's Discord integration. Your users can
                      see you take reliability seriously.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 7 */}
              <div>
                <div className="flex gap-4 mb-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                    7
                  </div>
                  <div>
                    <h3 className="font-sans font-semibold text-lg">
                      Create a status page
                    </h3>
                    <p className="text-lg leading-[1.7] text-body mt-1">
                      Go to the Status Pages section and create one. Add your
                      critical monitors to it. Share the URL in your app's footer
                      or help docs. When users report issues, they can check the
                      status page first. This saves you from answering "is the
                      site down?" messages at 2am.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Monitor allocation strategy */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              How to allocate your monitors
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Here's a practical allocation strategy for a typical side project
              stack. Adjust based on what matters most for your specific project.
            </p>
            <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">
                      Tier
                    </th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">
                      Interval
                    </th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">
                      Count
                    </th>
                    <th className="pb-3 pl-3 text-left font-medium text-muted-foreground">
                      Suggested use
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-3 pr-4 font-medium text-status-up">Fast</td>
                    <td className="py-3 px-3 text-center text-muted-foreground">30s</td>
                    <td className="py-3 px-3 text-center text-muted-foreground">4</td>
                    <td className="py-3 pl-3 text-muted-foreground">
                      Homepage, app dashboard, API health, payment endpoint
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 pr-4 font-medium text-status-degraded">Medium</td>
                    <td className="py-3 px-3 text-center text-muted-foreground">1 min</td>
                    <td className="py-3 px-3 text-center text-muted-foreground">3</td>
                    <td className="py-3 pl-3 text-muted-foreground">
                      Pricing page, docs, second project homepage
                    </td>
                  </tr>
                  <tr className="border-b last:border-0">
                    <td className="py-3 pr-4 font-medium text-primary">Standard</td>
                    <td className="py-3 px-3 text-center text-muted-foreground">3 min</td>
                    <td className="py-3 px-3 text-center text-muted-foreground">3</td>
                    <td className="py-3 pl-3 text-muted-foreground">
                      Third-party dependencies, secondary pages, cron monitors
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-lg leading-[1.7] text-body">
              If you have multiple side projects, distribute monitors across them.
              Split monitors across projects based on priority.
              Prioritize the project with actual users or revenue.
            </p>
          </section>

          {/* Common hosting + monitoring tips */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Hosting-specific monitoring tips
            </h2>
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Code className="h-4 w-4 text-primary" />
                  <h3 className="font-sans font-semibold">Vercel / Netlify (static sites)</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  These rarely go down, but edge function cold starts can cause
                  timeouts. Monitor your server-rendered routes and API routes
                  specifically, not just the static homepage. Set a response time
                  threshold to catch cold start issues.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Code className="h-4 w-4 text-primary" />
                  <h3 className="font-sans font-semibold">Railway / Render / Fly.io</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  Free and hobby tiers often hibernate after inactivity. Your
                  monitoring checks double as keep-alive pings — with 1-minute
                  checks, your app stays warm. Monitor the health endpoint, not
                  just the root URL, to catch database connection failures after
                  hibernation wake-up.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Code className="h-4 w-4 text-primary" />
                  <h3 className="font-sans font-semibold">VPS (DigitalOcean, Hetzner, Linode)</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  You manage everything. SSL renewals, process crashes, disk
                  space, memory. Monitor your app endpoint plus set up a cron
                  monitor for any scheduled maintenance scripts. If your VPS
                  runs out of disk space, your app crashes with no automatic
                  recovery.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Code className="h-4 w-4 text-primary" />
                  <h3 className="font-sans font-semibold">Supabase / PlanetScale (managed DB)</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  Your database provider can go down independently from your app.
                  Use a health endpoint that queries the database, not just
                  returns a static response. If the health check hits the DB and
                  returns OK, you know both your app and database are working.
                </p>
              </div>
            </div>
          </section>

          {/* What to do when you get an alert */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              What to do when you get an alert
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              You set up monitoring. Your phone buzzes. Now what? Here's a
              quick triage checklist for side project outages.
            </p>
            <div className="space-y-3">
              {[
                'Check the Uptrack dashboard for error details (status code, response time, error message)',
                'SSH into your server or check your hosting provider\'s dashboard for service status',
                'Check your application logs for the root cause',
                'If it\'s a deployment issue, roll back to the last working version',
                'If it\'s a hosting provider outage, update your status page so users know',
                'After resolving, check your monitor\'s history to see when the outage started and how long it lasted',
                'Document what happened and what you\'d do differently — even for side projects, a 30-second postmortem helps',
              ].map((step, i) => (
                <div key={i} className="flex gap-3">
                  <Rocket className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <p className="text-lg leading-[1.7] text-body">{step}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <Globe className="mx-auto mb-4 h-8 w-8 text-primary" />
            <h2 className="font-sans mb-3 text-xl font-semibold">
              Your Side Project Deserves Monitoring
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Pro includes 50 monitors at 30-second checks.
              Start free, or choose a plan for more.
            </p>
            <Button size="lg" asChild>
              <Link to="/pricing">
                See plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </article>
      </>

      </>
  )
}
